import Button from '@mui/material/Button'
import { useMutation } from '@tanstack/react-query'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import { getRouteName } from 'global/helpers/activeRoute'
import { analyticsEvent } from 'global/helpers/analytics'
import { handleFormSubmission, processServerErrorMessages } from 'global/helpers/form'
import { type ReactElement, useEffect, useState } from 'react'
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import {
  getUpdatedStopForAttachFiles,
  updateTripLegsCacheWithUpdatedStops,
} from 'screens/StatusManager/StatusManager.helpers'
import { TRIPS_QUERY_KEY } from 'screens/TripDashboard/TripDashboard.service'
import type { TripData } from 'screens/TripDashboard/TripDashboard.types'
import { TRIP_DETAILS_QUERY_KEY, tripLegsQueryOptions } from 'screens/TripDetails/TripDetails.service'
import type { LegData, StopData } from 'screens/TripDetails/TripDetails.types'
import DocumentsUploadFields from './DocumentsUploadFields'
import { attachFiles } from './StatusManager.service'
import type { DocumentsUploadFormValues } from './StatusManager.types'

interface DocumentsUploadDialogPayload {
  trip: TripData
  leg: LegData
  stop: StopData
  weblinkToken?: string
}

interface DocumentsUploadDialogProps {
  payload: DocumentsUploadDialogPayload
  open: boolean
  onAction: () => void
  onClose: () => void
}

const DocumentsUploadDialog = ({
  payload,
  open,
  onAction,
  onClose,
}: DocumentsUploadDialogProps): ReactElement => {
  const { t } = useTranslation()
  const location = useLocation()
  const [pendingFiles, setPendingFiles] = useState(false)
  const form = useForm<DocumentsUploadFormValues>({
    criteriaMode: 'all',
  })
  const { handleSubmit, reset, formState, setError, watch } = form
  const uploadedFiles = watch()
  const hasUploadedFiles = [
    uploadedFiles.popFiles?.length,
    uploadedFiles.podFiles?.length,
    uploadedFiles.photoFiles?.length,
    uploadedFiles.palletFile,
    uploadedFiles.troFile,
  ].some(Boolean)
  const isUploadDisabled = !hasUploadedFiles || pendingFiles || formState.isSubmitting
  const attachFilesMutation = useMutation({
    mutationFn: (data: DocumentsUploadFormValues) =>
      attachFiles(payload.trip.entityId, payload.stop.wayPointCid, data, payload.weblinkToken),
    onSuccess: async (_response, data, _result, context) => {
      const updatedStop = getUpdatedStopForAttachFiles(payload.stop, data)
      const queryKey = tripLegsQueryOptions(payload.trip.entityId, payload.weblinkToken).queryKey
      await updateTripLegsCacheWithUpdatedStops([updatedStop], queryKey, context.client)
      setTimeout(() => {
        void Promise.all([
          context.client.invalidateQueries({ queryKey: [TRIP_DETAILS_QUERY_KEY, payload.trip.entityId] }),
          context.client.invalidateQueries({ queryKey: [TRIPS_QUERY_KEY] }),
        ])
      }, 2000)
    },
  })

  useEffect(() => {
    if (open) {
      reset()
    }
  }, [open])

  const onSubmit: SubmitHandler<DocumentsUploadFormValues> = async (data: DocumentsUploadFormValues) => {
    await handleFormSubmission(() => attachFilesMutation.mutateAsync(data), {
      onError: (error) => setError('root', processServerErrorMessages(error)),
      onSuccess: () => {
        onAction()
        analyticsEvent('polestar_cs_documents_uploaded', [getRouteName(location.pathname)])
      },
    })
  }

  return (
    <FormProvider {...form}>
      <KNDialog
        open={open}
        onClose={onClose}
        closeLabel={t('general.cancel')}
        preventClosing={formState.isSubmitting}
        title={`${payload.leg.shipmentNumber} — ${t('screens.cs.trip_details.card.actions.upload_documents')}`}
        actions={
          <Button
            type="submit"
            color="secondary"
            variant="contained"
            disabled={isUploadDisabled}
            loading={formState.isSubmitting || pendingFiles}
            onClick={handleSubmit(onSubmit)}
          >
            {t('screens.cs.trip_details.card.actions.upload_documents')}
          </Button>
        }
        onSubmit={handleSubmit(onSubmit)}
      >
        <KNDialogFormErrors errors={formState.errors?.root} />
        <DocumentsUploadFields
          stopType={payload.stop.type}
          showProof
          hasProof={payload.stop.isProofAttached}
          showPhotos={payload.trip.sendingApplication !== 'MACADAM'}
          showPalletsDocument={payload.stop.palletsDocumentRequired}
          hasPalletsDocument={payload.stop.palletsDocumentAttached}
          showTro={payload.leg.troAvailable}
          hasTro={payload.stop.documents?.find((document) => document.fileType === 'TRO') !== undefined}
          onPending={setPendingFiles}
          weblinkToken={payload.weblinkToken}
        />
      </KNDialog>
    </FormProvider>
  )
}

export default DocumentsUploadDialog
